import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ProyectoCode } from '@/types/sitePlan';
import type { LoteKanban } from '@/types';

// Timeline historico de un lote. El query se desactiva cuando idLote es
// null para evitar fetchear con el modal cerrado. Cuando cambia el estado
// del lote (PATCH /api/lotes/{id}/estado) bumpamos esta key.
export function useLoteHistorico(idLote: number | null) {
  return useQuery({
    queryKey: ['lote-historico', idLote],
    queryFn: () => api.lotes.historico(idLote!),
    enabled: !!idLote,
  });
}

// Catalogo de Modelos para el dropdown del editor de Modelo Tramitado.
// Filtrado por proyecto en el backend. Cache largo: la lista no cambia
// seguido.
export function useModelos(proyecto: ProyectoCode | 'VC' | null) {
  return useQuery({
    queryKey: ['modelos', proyecto],
    queryFn: () => api.modelos.list(proyecto!),
    enabled: !!proyecto,
    staleTime: 10 * 60 * 1000,
  });
}

// Mutation para el Modelo Tramitado del lote. Optimistic update: la
// tarjeta refleja el nuevo modelo al toque (parchea modeloTramitado en
// el cache); on success reemplaza con la respuesta del backend (que
// trae el nombre del modelo resuelto via V_LotesSVG). On error rollback.
export function useChangeModeloTramitado(proyecto: ProyectoCode | null) {
  const qc = useQueryClient();
  const key = ['lotes-kanban', proyecto] as const;

  return useMutation({
    mutationFn: ({ idLote, idModDefecto }: { idLote: number; idModDefecto: number | null; nombreOptimista: string | null }) =>
      api.lotes.changeModeloTramitado(idLote, idModDefecto),

    onMutate: async ({ idLote, nombreOptimista }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<LoteKanban[]>(key);

      qc.setQueryData<LoteKanban[]>(key, list =>
        (list ?? []).map(l =>
          l.idLote === idLote
            ? { ...l, modeloTramitado: nombreOptimista }
            : l
        )
      );

      return { previous };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },

    onSuccess: (updated) => {
      qc.setQueryData<LoteKanban[]>(key, list =>
        (list ?? []).map(l => (l.idLote === updated.idLote ? updated : l))
      );
      // El cambio queda en LotesHistorico (lo escribio el trigger);
      // invalidamos el timeline para que aparezca si el modal lo abre.
      qc.invalidateQueries({ queryKey: ['lote-historico', updated.idLote] });
    },
  });
}

// Mutations para los campos de texto editables del lote (CodigoAPC,
// PermisoConstruccion). Mismo patron que useChangeModeloTramitado:
// optimistic update parcheando solo el campo, on success reemplaza con
// la respuesta del backend, on error rollback. Generico para no duplicar
// la misma logica con cada campo nuevo.
function useChangeLoteTextField<K extends 'codigoApc' | 'permisoConstruccion'>(
  proyecto: ProyectoCode | null,
  field: K,
  mutationFn: (idLote: number, value: string | null) => Promise<LoteKanban>,
) {
  const qc = useQueryClient();
  const key = ['lotes-kanban', proyecto] as const;

  return useMutation({
    mutationFn: ({ idLote, value }: { idLote: number; value: string | null }) =>
      mutationFn(idLote, value),

    onMutate: async ({ idLote, value }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<LoteKanban[]>(key);
      qc.setQueryData<LoteKanban[]>(key, list =>
        (list ?? []).map(l => (l.idLote === idLote ? { ...l, [field]: value } : l))
      );
      return { previous };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },

    onSuccess: (updated) => {
      qc.setQueryData<LoteKanban[]>(key, list =>
        (list ?? []).map(l => (l.idLote === updated.idLote ? updated : l))
      );
      // El trigger registro el cambio en LotesHistorico — invalida el
      // timeline para que aparezca si el modal lo abre.
      qc.invalidateQueries({ queryKey: ['lote-historico', updated.idLote] });
    },
  });
}

export function useChangeCodigoApc(proyecto: ProyectoCode | null) {
  return useChangeLoteTextField(proyecto, 'codigoApc', api.lotes.changeCodigoApc);
}

export function useChangePermisoConstruccion(proyecto: ProyectoCode | null) {
  return useChangeLoteTextField(
    proyecto, 'permisoConstruccion', api.lotes.changePermisoConstruccion
  );
}

// Data del Kanban de tramites CFIA filtrada por proyecto. El backend
// requiere el proyecto, asi que el query queda disabled hasta que se
// elija uno (en la practica siempre hay uno seleccionado por default).
export function useLotesKanban(proyecto: ProyectoCode | null) {
  return useQuery({
    queryKey: ['lotes-kanban', proyecto],
    queryFn: ({ signal }) => api.lotes.kanbanList(proyecto!, signal),
    enabled: !!proyecto,
  });
}

// Mover un lote a otro estado. Optimistic update: la tarjeta se mueve
// de columna al instante (parche local sobre el cache) y se reconcilia
// con la respuesta del backend (que trae la tarjeta enriquecida con el
// estado, color, dias, etc.). En caso de error rollback al snapshot.
export function useChangeEstadoLote(proyecto: ProyectoCode | null) {
  const qc = useQueryClient();
  const key = ['lotes-kanban', proyecto] as const;

  return useMutation({
    mutationFn: ({ idLote, idEstadoLote }: { idLote: number; idEstadoLote: number; idFaseLote: number }) =>
      api.lotes.changeEstado(idLote, idEstadoLote),

    onMutate: async ({ idLote, idEstadoLote, idFaseLote }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<LoteKanban[]>(key);

      // Patch optimista minimo: solo cambia idFaseLote + idEstadoLoteActual
      // y limpia los campos que el backend va a re-poblar (nombre/color/dias).
      // No intentamos predecir los datos del nuevo estado — la respuesta del
      // PATCH los trae limpios.
      qc.setQueryData<LoteKanban[]>(key, list =>
        (list ?? []).map(l =>
          l.idLote === idLote
            ? {
                ...l,
                idFaseLote,
                idEstadoLoteActual: idEstadoLote,
                // Marcamos un dia en estado = 0 para que el semaforo no aparezca
                // raro mientras llega la confirmacion.
                diasEnEstado: 0,
              }
            : l
        )
      );

      return { previous };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },

    onSuccess: (updated) => {
      // Reemplazamos la tarjeta con la version definitiva del backend
      // (incluye nombre del estado, color, etapa, etc.).
      qc.setQueryData<LoteKanban[]>(key, list =>
        (list ?? []).map(l => (l.idLote === updated.idLote ? updated : l))
      );
      // El cambio agrega una fila al historico — si el modal de detalle
      // esta abierto sobre este lote, queremos que recargue el timeline.
      qc.invalidateQueries({ queryKey: ['lote-historico', updated.idLote] });
    },
  });
}
